/**
 * src/lib/ingestUrlRecovery.js — RC 1.49.1 P0-1
 *
 * Wraps `POST /api/jobs/ingest-url` with the same 30s ingress
 * proxy 504 recovery semantics that protect CV / Cover Letter
 * generation. The backend's `find_duplicate_job_by_url` dedupe
 * step happens BEFORE any credit charge, so retrying the same
 * POST is safe: a successful background completion of the
 * original (504'd) call surfaces on retry as `{...row,
 * duplicate: true}` without a second charge.
 *
 * Hard rails honoured: zero schema, scoring, prompts, model
 * routing, or pricing changes. Frontend-only adjustment.
 */

import { apiFetch } from './api';
import { isTimeoutError } from './timeoutRecovery';

/**
 * @param {object} body                  POST body for `/api/jobs/ingest-url`.
 * @param {object} [opts]
 * @param {(state: 'idle'|'checking'|'recovered'|'unknown') => void} [opts.onState]
 *   UI banner hook. The wrapper drives it through:
 *   `idle` (default before the call) → `checking` (post-504, before
 *   retry) → `recovered` (retry succeeded) | `unknown` (retries
 *   exhausted; row may or may not have saved).
 * @param {number} [opts.maxRetries=2]
 * @param {number} [opts.intervalMs=8000]
 * @returns {Promise<object>} The saved (or recovered-duplicate) job row.
 * @throws  {Error} on non-timeout errors immediately, or on the
 *                  original 504 after recovery exhausts.
 */
export async function submitIngestUrlWithRecovery(body, opts = {}) {
  const { onState, maxRetries = 2, intervalMs = 8000 } = opts;
  try {
    return await apiFetch('/api/jobs/ingest-url', { method: 'POST', body });
  } catch (err) {
    if (!isTimeoutError(err)) throw err;
    onState?.('checking');
    for (let attempt = 0; attempt < maxRetries; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, intervalMs));
      try {
        // eslint-disable-next-line no-await-in-loop
        const recovered = await apiFetch('/api/jobs/ingest-url', { method: 'POST', body });
        onState?.('recovered');
        return recovered;
      } catch (retryErr) {
        if (!isTimeoutError(retryErr)) throw retryErr;
      }
    }
    onState?.('unknown');
    throw err;
  }
}
